// components/admin-panel-pages/AboutFeaturesManager.jsx - সম্পূর্ণ আপডেটেড
import React, { useState, useEffect } from 'react';
import { useAdmin } from '../context/AdminContext';

const AboutFeaturesManager = ({ setMessage }) => {
    const { apiRequest } = useAdmin();
    const [features, setFeatures] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editingFeature, setEditingFeature] = useState(null);
    const [saving, setSaving] = useState(false);

    const [formData, setFormData] = useState({
        title: '',
        icon: 'fa fa-arrow-right text-primary me-2',
        order: 0,
        isActive: true
    });

    useEffect(() => {
        loadFeatures();
    }, []);

    const loadFeatures = async () => {
        try {
            setLoading(true);
            const data = await apiRequest('/about-features/admin/all');
            if (data.success) {
                setFeatures(data.features || []);
            }
        } catch (error) {
            console.error('Error loading features:', error);

            // যদি API কাজ না করে, তবে ডেমো ডেটা দেখান
            if (error.message.includes('404') || error.message.includes('Failed to fetch')) {
                setMessage({
                    type: 'error',
                    text: 'About features API not found. Please check server setup.'
                });
                // ডেমো ডেটা লোড করুন
                setFeatures(getDemoFeatures());
            } else {
                setMessage({ type: 'error', text: 'Error loading features: ' + error.message });
            }
        } finally {
            setLoading(false);
        }
    };

    // ডেমো ফিচার ডেটা
    const getDemoFeatures = () => [
        { _id: '1', title: 'First Class Flights', icon: 'fa fa-arrow-right text-primary me-2', order: 1, isActive: true },
        { _id: '2', title: 'Handpicked Hotels', icon: 'fa fa-arrow-right text-primary me-2', order: 2, isActive: true },
        { _id: '3', title: '5 Star Accommodations', icon: 'fa fa-arrow-right text-primary me-2', order: 3, isActive: true },
        { _id: '4', title: 'Latest Model Vehicles', icon: 'fa fa-arrow-right text-primary me-2', order: 4, isActive: true },
        { _id: '5', title: '150 Premium City Tours', icon: 'fa fa-arrow-right text-primary me-2', order: 5, isActive: true },
        { _id: '6', title: '24/7 Service', icon: 'fa fa-arrow-right text-primary me-2', order: 6, isActive: true }
    ];

    const handleEdit = (feature) => {
        console.log('Editing feature:', feature);
        setEditingFeature(feature);
        setFormData({
            title: feature.title,
            icon: feature.icon,
            order: feature.order,
            isActive: feature.isActive
        });
        setShowForm(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        try {
            if (editingFeature) {
                console.log('Updating feature:', editingFeature._id, formData);
                const response = await apiRequest(`/about-features/${editingFeature._id}`, {
                    method: 'PUT',
                    body: formData
                });

                if (response.success) {
                    setMessage({ type: 'success', text: 'Feature updated successfully!' });
                    // আইটেম আপডেট করুন
                    setFeatures(prev => prev.map(f =>
                        f._id === editingFeature._id ? { ...f, ...formData } : f
                    ));
                }
            } else {
                console.log('Creating new feature:', formData);
                const response = await apiRequest('/about-features', {
                    method: 'POST',
                    body: formData
                });

                if (response.success) {
                    setMessage({ type: 'success', text: 'Feature created successfully!' });
                    // নতুন আইটেম যোগ করুন
                    setFeatures(prev => [...prev, { _id: response.feature._id, ...formData }]);
                }
            }

            setShowForm(false);
            setEditingFeature(null);
            resetForm();

        } catch (error) {
            console.error('Save feature error:', error);
            setMessage({
                type: 'error',
                text: `Error ${editingFeature ? 'updating' : 'creating'} feature: ${error.message}`
            });
        } finally {
            setSaving(false);
        }
    };

    const resetForm = () => {
        setFormData({
            title: '',
            icon: 'fa fa-arrow-right text-primary me-2',
            order: features.length + 1,
            isActive: true
        });
    };

    const handleDelete = async (featureId, featureTitle) => {
        if (window.confirm(`Are you sure you want to delete "${featureTitle}"?`)) {
            try {
                console.log('Deleting feature:', featureId);
                const response = await apiRequest(`/about-features/${featureId}`, {
                    method: 'DELETE'
                });

                if (response.success) {
                    setMessage({ type: 'success', text: 'Feature deleted successfully!' });
                    // আইটেম রিমুভ করুন
                    setFeatures(prev => prev.filter(f => f._id !== featureId));
                }
            } catch (error) {
                console.error('Delete feature error:', error);
                setMessage({ type: 'error', text: 'Error deleting feature: ' + error.message });
            }
        }
    };

    const handleToggleActive = async (feature) => {
        try {
            const newStatus = !feature.isActive;
            console.log('Toggling feature:', feature._id, 'to', newStatus);

            const response = await apiRequest(`/about-features/${feature._id}`, {
                method: 'PUT',
                body: { isActive: newStatus }
            });

            if (response.success) {
                setMessage({
                    type: 'success',
                    text: `Feature ${newStatus ? 'activated' : 'deactivated'} successfully!`
                });
                // স্ট্যাটাস আপডেট করুন
                setFeatures(prev => prev.map(f =>
                    f._id === feature._id ? { ...f, isActive: newStatus } : f
                ));
            }
        } catch (error) {
            console.error('Toggle feature error:', error);
            setMessage({ type: 'error', text: 'Error toggling feature: ' + error.message });
        }
    };

    const moveUp = async (index) => {
        if (index === 0) return;

        const updatedFeatures = [...features];
        const temp = updatedFeatures[index];
        updatedFeatures[index] = updatedFeatures[index - 1];
        updatedFeatures[index - 1] = temp;

        // অর্ডার নাম্বার আপডেট করুন
        const reordered = updatedFeatures.map((f, i) => ({ ...f, order: i + 1 }));
        setFeatures(reordered);

        try {
            const orderedIds = reordered.map(f => f._id);
            const response = await apiRequest('/about-features/reorder', {
                method: 'POST',
                body: { orderedIds }
            });

            if (response.success) {
                setMessage({ type: 'success', text: 'Order updated successfully!' });
            }
        } catch (error) {
            console.error('Error reordering:', error);
            setMessage({ type: 'error', text: 'Error saving new order' });
            loadFeatures(); // আসল অর্ডারে ফিরে যান
        }
    };

    const moveDown = async (index) => {
        if (index === features.length - 1) return;

        const updatedFeatures = [...features];
        const temp = updatedFeatures[index];
        updatedFeatures[index] = updatedFeatures[index + 1];
        updatedFeatures[index + 1] = temp;

        // অর্ডার নাম্বার আপডেট করুন
        const reordered = updatedFeatures.map((f, i) => ({ ...f, order: i + 1 }));
        setFeatures(reordered);

        try {
            const orderedIds = reordered.map(f => f._id);
            const response = await apiRequest('/about-features/reorder', {
                method: 'POST',
                body: { orderedIds }
            });

            if (response.success) {
                setMessage({ type: 'success', text: 'Order updated successfully!' });
            }
        } catch (error) {
            console.error('Error reordering:', error);
            setMessage({ type: 'error', text: 'Error saving new order' });
            loadFeatures(); // আসল অর্ডারে ফিরে যান
        }
    };

    const iconOptions = [
        { value: 'fa fa-arrow-right text-primary me-2', label: 'Arrow Right (Default)' },
        { value: 'fa fa-check text-success me-2', label: 'Check Mark' },
        { value: 'fa fa-star text-warning me-2', label: 'Star' },
        { value: 'fa fa-heart text-danger me-2', label: 'Heart' },
        { value: 'fa fa-plane text-info me-2', label: 'Plane' },
        { value: 'fa fa-hotel text-primary me-2', label: 'Hotel' },
        { value: 'fa fa-car text-success me-2', label: 'Car' },
        { value: 'fa fa-map-marker text-danger me-2', label: 'Map Marker' },
        { value: 'fa fa-clock text-info me-2', label: 'Clock' },
        { value: 'fa fa-award text-warning me-2', label: 'Award' },
        { value: 'fa fa-globe text-primary me-2', label: 'Globe' },
        { value: 'fa fa-umbrella-beach text-info me-2', label: 'Beach' }
    ];

    if (loading && !features.length) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2 text-muted">Loading features...</p>
            </div>
        );
    }

    return (
        <div className="container-fluid">
            {/* Header Section */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm">
                        <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                            <h4 className="mb-0">
                                <i className="fas fa-list-check me-2"></i>
                                About Page Features Management
                            </h4>
                            <button
                                className="btn btn-light btn-sm"
                                onClick={() => {
                                    setEditingFeature(null);
                                    resetForm();
                                    setShowForm(true);
                                }}
                                disabled={loading}
                            >
                                <i className="fas fa-plus me-1"></i>
                                Add New Feature
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Statistics */}
            <div className="row mb-4">
                <div className="col-md-4">
                    <div className="card bg-primary text-white">
                        <div className="card-body">
                            <div className="d-flex justify-content-between">
                                <div>
                                    <h4 className="mb-0">{features.length}</h4>
                                    <small>Total Features</small>
                                </div>
                                <i className="fas fa-list-check fa-2x opacity-50"></i>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-4">
                    <div className="card bg-success text-white">
                        <div className="card-body">
                            <div className="d-flex justify-content-between">
                                <div>
                                    <h4 className="mb-0">{features.filter(f => f.isActive).length}</h4>
                                    <small>Active Features</small>
                                </div>
                                <i className="fas fa-check-circle fa-2x opacity-50"></i>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-4">
                    <div className="card bg-warning text-dark">
                        <div className="card-body">
                            <div className="d-flex justify-content-between">
                                <div>
                                    <h4 className="mb-0">{features.filter(f => !f.isActive).length}</h4>
                                    <small>Inactive Features</small>
                                </div>
                                <i className="fas fa-pause-circle fa-2x opacity-50"></i>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Instructions */}
            <div className="alert alert-info mb-4">
                <i className="fas fa-info-circle me-2"></i>
                <strong>Instructions:</strong> Manage your About page features here. Active features will appear on the website.
                <div className="mt-2 small">
                    <span className="badge bg-success me-2">Active</span> - Visible on website
                    <span className="badge bg-warning ms-3 me-2">Inactive</span> - Hidden from website
                </div>
            </div>

            {/* Debug Info - Remove in production */}
            {features.length > 0 && (
                <div className="alert alert-warning mb-4">
                    <i className="fas fa-bug me-2"></i>
                    <strong>Debug Info:</strong> Showing {features.length} features. Check browser console for API errors.
                </div>
            )}

            {/* Features List */}
            <div className="card">
                <div className="card-header bg-light">
                    <h6 className="mb-0">
                        <i className="fas fa-list me-2"></i>
                        Features List ({features.length} items)
                    </h6>
                </div>
                <div className="card-body">
                    {features.length === 0 ? (
                        <div className="text-center py-5">
                            <i className="fas fa-list-check fa-3x text-muted mb-3"></i>
                            <h5 className="text-muted">No Features Found</h5>
                            <p className="text-muted">Get started by adding your first feature</p>
                            <button
                                className="btn btn-primary mt-3"
                                onClick={() => {
                                    setEditingFeature(null);
                                    resetForm();
                                    setShowForm(true);
                                }}
                            >
                                <i className="fas fa-plus me-1"></i>
                                Add First Feature
                            </button>
                        </div>
                    ) : (
                        <div className="list-group">
                            {features.map((feature, index) => (
                                <div key={feature._id || index} className={`list-group-item mb-2 ${!feature.isActive ? 'border-warning' : ''}`}>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div className="d-flex align-items-center">
                                            <div className="me-3">
                                                <button
                                                    className="btn btn-sm btn-outline-secondary me-1"
                                                    onClick={() => moveUp(index)}
                                                    disabled={index === 0 || loading}
                                                    title="Move Up"
                                                >
                                                    <i className="fas fa-arrow-up"></i>
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-outline-secondary"
                                                    onClick={() => moveDown(index)}
                                                    disabled={index === features.length - 1 || loading}
                                                    title="Move Down"
                                                >
                                                    <i className="fas fa-arrow-down"></i>
                                                </button>
                                            </div>
                                            <span className="badge bg-light text-dark me-3">
                                                {index + 1}
                                            </span>
                                            <div>
                                                <p className="mb-0">
                                                    <i className={`${feature.icon}`}></i>
                                                    <strong>{feature.title}</strong>
                                                </p>
                                                <small className="text-muted">
                                                    ID: {feature._id?.substring?.(0, 8) || feature._id} | Order: {feature.order}
                                                </small>
                                            </div>
                                        </div>
                                        <div className="d-flex gap-2">
                                            <span className={`badge ${feature.isActive ? 'bg-success' : 'bg-warning'}`}>
                                                {feature.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                            <button
                                                className="btn btn-sm btn-outline-primary"
                                                onClick={() => handleEdit(feature)}
                                                disabled={loading}
                                                title="Edit Feature"
                                            >
                                                <i className="fas fa-edit"></i>
                                            </button>
                                            <button
                                                className={`btn btn-sm ${feature.isActive ? 'btn-warning' : 'btn-success'}`}
                                                onClick={() => handleToggleActive(feature)}
                                                disabled={loading}
                                                title={feature.isActive ? 'Deactivate' : 'Activate'}
                                            >
                                                <i className={`fas ${feature.isActive ? 'fa-pause' : 'fa-play'}`}></i>
                                            </button>
                                            <button
                                                className="btn btn-sm btn-outline-danger"
                                                onClick={() => handleDelete(feature._id, feature.title)}
                                                disabled={loading}
                                                title="Delete Feature"
                                            >
                                                <i className="fas fa-trash"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Add/Edit Form Modal */}
            {showForm && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-md">
                        <div className="modal-content">
                            <div className="modal-header bg-primary text-white">
                                <h5 className="modal-title">
                                    <i className="fas fa-list-check me-2"></i>
                                    {editingFeature ? 'Edit Feature' : 'Add New Feature'}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => {
                                        setShowForm(false);
                                        setEditingFeature(null);
                                        resetForm();
                                    }}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <form onSubmit={handleSubmit}>
                                    <div className="mb-3">
                                        <label className="form-label">Feature Title *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={formData.title}
                                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                            required
                                            placeholder="e.g., First Class Flights, 24/7 Service"
                                            maxLength="100"
                                        />
                                        <div className="form-text">
                                            This will appear on the About page
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Icon *</label>
                                        <select
                                            className="form-select"
                                            value={formData.icon}
                                            onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                                            required
                                        >
                                            {iconOptions.map(option => (
                                                <option key={option.value} value={option.value}>
                                                    <i className={`${option.value.split(' ')[0]} ${option.value.split(' ')[1]} me-2`}></i>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                        <div className="form-text">
                                            Choose an icon to display before the feature
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Display Order</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={formData.order}
                                            onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                                            min="0"
                                            placeholder="Lower number appears first"
                                        />
                                        <div className="form-text">
                                            Features are sorted by this number (ascending)
                                        </div>
                                    </div>

                                    <div className="mb-4">
                                        <div className="form-check form-switch">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                checked={formData.isActive}
                                                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                            />
                                            <label className="form-check-label">
                                                Active (Visible on website)
                                            </label>
                                        </div>
                                    </div>

                                    <div className="modal-footer">
                                        <button
                                            type="button"
                                            className="btn btn-secondary"
                                            onClick={() => {
                                                setShowForm(false);
                                                setEditingFeature(null);
                                                resetForm();
                                            }}
                                            disabled={saving}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                            disabled={saving}
                                        >
                                            {saving ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                    {editingFeature ? 'Updating...' : 'Creating...'}
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fas fa-save me-2"></i>
                                                    {editingFeature ? 'Update Feature' : 'Create Feature'}
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AboutFeaturesManager;